import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { PageFunction } from '@unifyapps/defs/types/pageFunction';
import type { UnsavedDataSource } from '../../stores/DataSourceRecordStore';
import DataSourceHelper from '../../helper/DataSourceHelper';
import noCodeBuildDependencyGraph from '../../dependency/noCodeBuildDependencyGraph';
import { isNoCodeEntityId } from '../../utils/isNoCodeEntityId';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';
import { PageFunctionHelper } from '../../../helpers/PageFunctionHelper';
import {
  dedupeDynamicAndConditionalPathsHandler,
  dynamicAndConditionalPathsHandler,
} from './handlers';
import { ENTITY_OVERRIDES } from './const';

export const noCodeBuildDependencyGraphParams = {
  isValidEntity: isNoCodeEntityId,
  handlers: [dynamicAndConditionalPathsHandler],
  dedupeHandlers: [dedupeDynamicAndConditionalPathsHandler],
};

function getBlockEntityOverrides(block: BlockType) {
  const overrides: Record<string, unknown> = {};

  // we are adding visibility because it is not stored in block state, but we want to dependent on the dynamic pills mentioned in the visibility
  overrides.visibility = block.visibility;

  return overrides;
}

// no need to remove dependsOn etc. here because we don't pick them in block state
function blockDependencyBuilder({
  blockState,
  pageBlock,
}: {
  blockState: NonNullable<BlockStateUnionType>;
  pageBlock: BlockType;
}) {
  const overrides = getBlockEntityOverrides(pageBlock);

  const blockDependencies = noCodeBuildDependencyGraph({
    objects: {
      [blockState.id]: {
        ...blockState,
        // dynamic content and condition operators present inside the block are not added in dynPaths and condPaths,
        // so any {{ }} will be added in dependsOn but not in dyn and cond paths
        [ENTITY_OVERRIDES]: overrides,
      },
    },
    ...noCodeBuildDependencyGraphParams,
  });

  const blockDependsOn = DependencyHelper.getDependsOn(blockDependencies[blockState.id]) ?? [];

  return {
    [blockState.id]: {
      ...blockDependencies[blockState.id],
      dependsOn: blockDependsOn,
    },
  };
}

function pageFunctionDependencyBuilder({ pageFunction }: { pageFunction: PageFunction }) {
  const adaptedPageFunction =
    PageFunctionHelper.getAdaptedPageFunctionForDependencyGraph(pageFunction);
  return noCodeBuildDependencyGraph({
    objects: { [pageFunction.id]: adaptedPageFunction },
    ...noCodeBuildDependencyGraphParams,
  });
}

function dataSourceDependencyBuilder({ dataSource }: { dataSource: UnsavedDataSource }) {
  const { adaptedDataSources } = DataSourceHelper.getAdaptedDataSourcesForDependencyGraph({
    [dataSource.id]: dataSource,
  });

  return noCodeBuildDependencyGraph({
    objects: { [dataSource.id]: adaptedDataSources[dataSource.id] },
    ...noCodeBuildDependencyGraphParams,
  });
}

export { blockDependencyBuilder, pageFunctionDependencyBuilder, dataSourceDependencyBuilder };
